import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, addDoc, deleteDoc, getDocs } from 'firebase/firestore';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  addresses: Address[];
  recipients?: Recipient[];
}

interface Address {
  id: string;
  street: string;
  colonia?: string;
  city: string;
  state: string;
  zipCode: string;
  isDefault: boolean;
}

interface Recipient {
  id: string;
  name: string;
  street: string;
  colonia?: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  addAddress: (address: Omit<Address, 'id'>) => Promise<void>;
  deleteAddress: (addressId: string) => Promise<void>;
  addRecipient: (recipient: Omit<Recipient, 'id'>) => Promise<void>;
  deleteRecipient: (recipientId: string) => Promise<void>;
  updateRecipient: (recipientId: string, data: Partial<Recipient>) => Promise<void>;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setIsLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      try {
        if (fbUser) {
          let name = fbUser.displayName || '';
          let email = fbUser.email || '';
          let phone = '';

          if (db) {
            try {
              const userRef = doc(db, 'users', fbUser.uid);
              const snap = await getDoc(userRef);
              if (snap.exists()) {
                const data = snap.data() as { name?: string; email?: string; phone?: string };
                name = data.name ?? name;
                email = data.email ?? email;
                phone = data.phone ?? '';
              } else {
                await setDoc(userRef, { name, email, phone }, { merge: true });
              }

              const addrCol = collection(db, 'users', fbUser.uid, 'addresses');
              const addrSnap = await getDocs(addrCol);
              const addresses: Address[] = addrSnap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Address, 'id'>) }));

              const recCol = collection(db, 'users', fbUser.uid, 'recipients');
              const recSnap = await getDocs(recCol);
              const recipients: Recipient[] = recSnap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Recipient, 'id'>) }));

              setUser({ id: fbUser.uid, name, email, phone, addresses, recipients });
            } catch (err) {
              console.warn('[Auth] Falló carga de perfil/colecciones, usando usuario mínimo', err);
              setUser({ id: fbUser.uid, name, email, phone, addresses: [], recipients: [] });
            }
          } else {
            setUser({ id: fbUser.uid, name, email, phone, addresses: [], recipients: [] });
          }
        } else {
          setUser(null);
        }
      } catch (e) {
        console.warn('[Auth] Error inesperado en onAuthStateChanged', e);
        // En caso de error inesperado, reflejar estado sin datos si hay currentUser
        if (auth.currentUser) {
          const fb = auth.currentUser;
          setUser({ id: fb.uid, name: fb.displayName || '', email: fb.email || '', phone: '', addresses: [], recipients: [] });
        } else {
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      if (!auth) throw new Error('Firebase no está configurado');
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Error al iniciar sesión');
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      if (!auth) throw new Error('Firebase no está configurado');
      const cred = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      // Actualizar displayName
      if (cred.user) {
        await updateProfile(cred.user, { displayName: userData.name });
        // Guardar perfil base en Firestore
        if (db) {
          const userRef = doc(db, 'users', cred.user.uid);
          await setDoc(userRef, { name: userData.name, email: userData.email, phone: userData.phone }, { merge: true });
        }

        // Enviar correo de bienvenida via backend
        try {
          // El endpoint /api/test/send-welcome está mapeado en Nginx a server:3001/test/send-welcome
          fetch('/api/test/send-welcome', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ to: userData.email, name: userData.name }),
          }).catch(err => console.warn('[Auth] Error enviando bienvenida', err));
        } catch (e) {
          console.warn('[Auth] Error invocando bienvenida', e);
        }
      }
    } catch (error) {
      throw new Error('Error al registrar usuario');
    }
  };

  const logout = async () => {
    try {
      if (!auth) throw new Error('Firebase no está configurado');
      await signOut(auth);
      setUser(null);
    } catch (error) {
      throw new Error('Error al cerrar sesión');
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      if (!auth || !auth.currentUser) throw new Error('Usuario no autenticado');
      const updatedUser = { ...user, ...userData } as User;
      // Reflejar displayName si cambió
      if (userData.name) {
        await updateProfile(auth.currentUser, { displayName: userData.name });
      }
      // Actualizar documento de perfil en Firestore
      if (db) {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const payload: { name?: string; phone?: string } = {};
        if (userData.name !== undefined) payload.name = userData.name;
        if (userData.phone !== undefined) payload.phone = userData.phone;
        if (Object.keys(payload).length) {
          await updateDoc(userRef, payload);
        }
      }
      setUser(updatedUser);
    } catch (error) {
      throw new Error('Error al actualizar usuario');
    }
  };

  const addAddress = async (address: Omit<Address, 'id'>) => {
    try {
      if (!user) throw new Error('Usuario no autenticado');
      if (db) {
        const addrCol = collection(db, 'users', user.id, 'addresses');
        const added = await addDoc(addrCol, address);
        const newAddress: Address = { id: added.id, ...address };
        setUser({ ...user, addresses: [...user.addresses, newAddress] });
      } else {
        const fallback: Address = { ...address, id: Date.now().toString() };
        setUser({ ...user, addresses: [...user.addresses, fallback] });
      }
    } catch (error) {
      throw new Error('Error al agregar dirección');
    }
  };

  const addRecipient = async (recipient: Omit<Recipient, 'id'>) => {
    try {
      if (!user) throw new Error('Usuario no autenticado');
      if (db) {
        const recCol = collection(db, 'users', user.id, 'recipients');
        const added = await addDoc(recCol, recipient);
        const newRecipient: Recipient = { id: added.id, ...recipient };
        setUser({ ...user, recipients: [...(user.recipients || []), newRecipient] });
      } else {
        const fallback: Recipient = { ...recipient, id: Date.now().toString() };
        setUser({ ...user, recipients: [...(user.recipients || []), fallback] });
      }
    } catch (error) {
      throw new Error('Error al agregar destinatario');
    }
  };

  const deleteRecipient = async (recipientId: string) => {
    try {
      if (!user) throw new Error('Usuario no autenticado');
      if (db) {
        const recRef = doc(db, 'users', user.id, 'recipients', recipientId);
        await deleteDoc(recRef);
      }
      const current = user.recipients || [];
      setUser({ ...user, recipients: current.filter(r => r.id !== recipientId) });
    } catch (error) {
      throw new Error('Error al eliminar destinatario');
    }
  };

  const updateRecipient = async (recipientId: string, data: Partial<Recipient>) => {
    try {
      if (!user) throw new Error('Usuario no autenticado');
      if (db) {
        const recRef = doc(db, 'users', user.id, 'recipients', recipientId);
        const payload: Partial<Recipient> = {};
        if (data.name !== undefined) payload.name = data.name;
        if (data.street !== undefined) payload.street = data.street;
        if (data.colonia !== undefined) payload.colonia = data.colonia;
        if (data.city !== undefined) payload.city = data.city;
        if (data.state !== undefined) payload.state = data.state;
        if (data.zipCode !== undefined) payload.zipCode = data.zipCode;
        if (data.phone !== undefined) payload.phone = data.phone;
        await updateDoc(recRef, payload as any);
      }
      const current = user.recipients || [];
      const updated = current.map(r => r.id === recipientId ? { ...r, ...data } : r);
      setUser({ ...user, recipients: updated });
    } catch (error) {
      throw new Error('Error al actualizar destinatario');
    }
  };

  const deleteAddress = async (addressId: string) => {
    try {
      if (!user) throw new Error('Usuario no autenticado');
      if (db) {
        const addrRef = doc(db, 'users', user.id, 'addresses', addressId);
        await deleteDoc(addrRef);
      }
      setUser({ ...user, addresses: user.addresses.filter(addr => addr.id !== addressId) });
    } catch (error) {
      throw new Error('Error al eliminar dirección');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        register,
        logout,
        updateUser,
        addAddress,
        deleteAddress,
        addRecipient,
        deleteRecipient,
        updateRecipient
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};